<template>
  <div :class="wrapperClass">
    <label
      v-if="label"
      :for="inputId"
      :class="labelClass"
    >{{ label }}<span v-if="required">*</span></label>

    <div :class="resolvedControlClass">
      <button
        type="button"
        :class="buttonClass + ' rounded-s-lg'"
        :disabled="disabled || !canDecrement"
        :aria-label="decrementAriaLabel"
        @click="decrement"
      >
        <svg class="w-3 h-3 text-custom_gray" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 2"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 1h16"/></svg>
      </button>

      <input
        :id="inputId"
        :name="name || undefined"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        :value="displayValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :aria-label="ariaLabel"
        :aria-valuemin="min"
        :aria-valuemax="max"
        :aria-valuenow="currentValue"
        :class="inputClass"
        @input="onInput"
        @keydown="onKeydown"
        @blur="onBlur"
      />

      <button
        type="button"
        :class="buttonClass + ' rounded-e-lg'"
        :disabled="disabled || !canIncrement"
        :aria-label="incrementAriaLabel"
        @click="increment"
      >
        <svg class="w-3 h-3 text-custom_gray" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 18"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 1v16M1 9h16"/></svg>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

let uid = 0

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  min: {
    type: Number,
    default: 0
  },
  max: {
    type: Number,
    default: null
  },
  step: {
    type: Number,
    default: 1
  },
  placeholder: {
    type: String,
    default: '0'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  inputId: {
    type: String,
    default: ''
  },
  name: {
    type: String,
    default: ''
  },
  widthClass: {
    type: String,
    default: 'w-full sm:max-w-[8rem]'
  },
  wrapperClass: {
    type: String,
    default: 'w-auto'
  },
  labelClass: {
    type: String,
    default: 'block mb-2 text-sm font-medium text-custom_gray sm:min-h-[2.5rem]'
  },
  controlClass: {
    type: String,
    default: ''
  },
  buttonClass: {
    type: String,
    default: 'bg-gray-100 hover:bg-gray-200 disabled:cursor-not-allowed border border-gray-300 p-3 h-9 focus:outline-none focus:ring-2 focus:ring-gray-100'
  },
  inputClass: {
    type: String,
    default: 'bg-gray-50 border-x-0 border border-gray-300 h-9 text-center text-custom_gray text-sm focus:ring-0 focus:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 block w-full py-2.5'
  }
})

const emit = defineEmits(['update:modelValue', 'change', 'blur'])

const generatedId = `number-stepper-${++uid}`

const sanitize = (value) => String(value ?? '').replace(/[^0-9]/g, '')

const clamp = (value) => {
  let result = value
  if (props.min !== null && result < props.min) result = props.min
  if (props.max !== null && result > props.max) result = props.max
  return result
}

const displayValue = ref(sanitize(props.modelValue))

watch(() => props.modelValue, (newValue) => {
  displayValue.value = sanitize(newValue)
})

const currentValue = computed(() => {
  const parsed = parseInt(displayValue.value, 10)
  return isNaN(parsed) ? 0 : parsed
})

const canDecrement = computed(() => {
  if (props.min === null || displayValue.value === '') return true
  return currentValue.value > props.min
})

const canIncrement = computed(() => {
  if (props.max === null || displayValue.value === '') return true
  return currentValue.value < props.max
})

const inputId = computed(() => props.inputId || generatedId)
const ariaLabel = computed(() => props.label || 'Valor numérico')

const resolvedControlClass = computed(() => {
  const base = 'flex items-center rounded-lg overflow-hidden'
  const classes = [base, props.widthClass, props.controlClass].filter(Boolean)
  return classes.join(' ')
})

const setValue = (value) => {
  if (props.disabled) return

  const previous = displayValue.value
  const next = value === null ? '' : String(clamp(value))

  displayValue.value = next
  emit('update:modelValue', next)

  if (next !== previous) {
    emit('change', { value: next, previous })
  }
}

function increment() {
  const base = displayValue.value === ''
    ? (props.min === null ? 0 : props.min)
    : currentValue.value

  setValue(base + props.step)
}

function decrement() {
  const base = displayValue.value === ''
    ? (props.min === null ? 0 : props.min)
    : currentValue.value

  setValue(base - props.step)
}

function onInput(event) {
  if (props.disabled) return

  const cleaned = sanitize(event.target.value)
  const next = cleaned === '' ? '' : String(clamp(parseInt(cleaned, 10)))
  const previous = displayValue.value

  displayValue.value = next

  if (event.target.value !== next) {
    event.target.value = next
  }

  emit('update:modelValue', next)

  if (next !== previous) {
    emit('change', { value: next, previous })
  }
}

function onKeydown(event) {
  if (event.key === 'ArrowUp') {
    event.preventDefault()
    increment()
  } else if (event.key === 'ArrowDown') {
    event.preventDefault()
    decrement()
  }
}

function onBlur(event) {
  emit('blur', event)
}

function focus() {
  const element = document.getElementById(inputId.value)
  if (element) element.focus()
}

defineExpose({ increment, decrement, focus })
</script>
